import { useCallback, useState } from "react";

/**
 * State + handlers for the floating "Tell AI" Generate Messages panel
 * ({@link FloatingGeneratePanel}), extracted from {@link useAddInboxChat} to keep
 * that hook under the file-length gate.
 *
 * Closing while a generation is running is safe: the job runs server-side and the
 * page-level copywriting socket keeps tracking it, so the result still lands in
 * the chat (and in list_person) after the panel is gone.
 */
export const useGeneratePanelState = <TDrafts>(
	injectAiDrafts: (drafts: TDrafts, contextPayload?: unknown) => void
) => {
	const [showGeneratePanel, setShowGeneratePanel] = useState<boolean>(false);
	const [generateTriggerDescriptionOverride, setGenerateTriggerDescriptionOverride] = useState<
		string | null
	>(null);

	const openGeneratePanel = useCallback((triggerDescription?: string | null) => {
		setGenerateTriggerDescriptionOverride(triggerDescription ?? null);
		setShowGeneratePanel(true);
	}, []);

	const handleRequestCloseGeneratePanel = useCallback(() => {
		setGenerateTriggerDescriptionOverride(null);
		setShowGeneratePanel(false);
	}, []);

	// Inject the generated drafts into the chat, then close the panel.
	const handleGenerationComplete = useCallback(
		(drafts: TDrafts, contextPayload?: unknown) => {
			injectAiDrafts(drafts, contextPayload);
			setGenerateTriggerDescriptionOverride(null);
			setShowGeneratePanel(false);
		},
		[injectAiDrafts]
	);

	return {
		showGeneratePanel,
		generateTriggerDescriptionOverride,
		openGeneratePanel,
		handleRequestCloseGeneratePanel,
		handleGenerationComplete,
	};
};
